import { message, Modal, Spin } from "antd";
import { get } from "lodash";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ProcessPicker from "screens/Private/Broker/Projects/List/Subscriptions/GenerateLinkModal/ProcessPicker";
import { useLazyQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { PROCESS_CONFIGS } from "utils/api/graphql/queries/process-config";
import { PROJECT_BROKER_TRACER_ID } from "utils/api/graphql/queries/projects";
import { TRACER, TRACERS } from "utils/api/graphql/queries/tracers";
import { PROCESS_CONFIG_TYPES, TRACER_TYPES } from "utils/constants";

const FALLBACK_TRACERS_TAKE = 80;

const STEPS = {
  IDLE: "IDLE",
  LOADING: "LOADING",
  PICK_PROCESS: "PICK_PROCESS",
};

const PROCESS_PICKER_CANCELLED = "PROCESS_PICKER_CANCELLED";

const uniqueB2cProcessesFromTracers = (tracersList) => {
  const map = new Map();
  tracersList.forEach((tr) => {
    const proc = get(tr, "flow.process");
    if (!proc?.id || map.has(proc.id)) {
      return;
    }
    map.set(proc.id, {
      id: proc.id,
      name: proc.name,
      theme: proc.theme,
    });
  });
  return [...map.values()];
};

const pickTracerForProcessId = (tracersList, processId) => {
  const matches = tracersList.filter(
    (tracer) => get(tracer, "flow.process.id") === processId,
  );
  return matches.find((tracer) => get(tracer, "source.id")) || matches[0];
};

const useQuoteProcessResolver = ({ projectId, tracerId, prospectTheme }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.PROJECT);
  const resolutionErrorMessage = t(
    "LIST.SUBSCRIPTION.GENERATE_SUBSCRIPTION_LINK.RESOLUTION_ERROR",
  );
  const pickProcessTitle =
    t("LIST.SUBSCRIPTION.GENERATE_SUBSCRIPTION_LINK.PICK_PROCESS_TITLE") ||
    "Choisir un parcours";

  const [step, setStep] = useState(STEPS.IDLE);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [associatedProcesses, setAssociatedProcesses] = useState([]);
  const pendingResolveRef = useRef(null);

  const [getTracer] = useLazyQuery(TRACER);
  const [getTracers] = useLazyQuery(TRACERS);
  const [getProcessConfigs] = useLazyQuery(PROCESS_CONFIGS);
  const [getProjectBrokerTracerId] = useLazyQuery(PROJECT_BROKER_TRACER_ID);

  const closeModal = useCallback(() => {
    setStep(STEPS.IDLE);
    setIsModalOpen(false);
    setAssociatedProcesses([]);
  }, []);

  const failResolution = useCallback(
    (error) => {
      message.error(resolutionErrorMessage);
      closeModal();
      const pending = pendingResolveRef.current;
      pendingResolveRef.current = null;
      if (pending) {
        pending.reject(error || new Error("PROCESS_RESOLUTION_FAILED"));
      }
    },
    [closeModal, resolutionErrorMessage],
  );

  const succeedResolution = useCallback(
    (tracer) => {
      closeModal();
      const pending = pendingResolveRef.current;
      pendingResolveRef.current = null;
      if (pending) {
        pending.resolve(tracer);
      }
    },
    [closeModal],
  );

  const openPicker = useCallback((processes) => {
    setAssociatedProcesses(processes);
    setStep(STEPS.PICK_PROCESS);
    setIsModalOpen(true);
  }, []);

  const loadTracerForProcess = useCallback(
    async (processRef) => {
      const processId = processRef?.id;
      const processTheme = processRef?.theme;
      if (!processId) {
        return null;
      }

      const primary = await getTracer({
        variables: {
          where: { flow: { process: { id: processId } } },
        },
        fetchPolicy: "network-only",
      });
      let tracer = primary.data?.tracer;
      if ((!tracer || !get(tracer, "source.id")) && processTheme) {
        const { data } = await getTracers({
          variables: {
            where: {
              flow: {
                process: {
                  theme: processTheme,
                },
              },
            },
            take: FALLBACK_TRACERS_TAKE,
          },
          fetchPolicy: "network-only",
        });
        const list = get(data?.tracers, "data", []);
        tracer = pickTracerForProcessId(list, processId) || tracer;
      }

      return tracer;
    },
    [getTracer, getTracers],
  );

  const resolveFromFallback = useCallback(async () => {
    const { data } = await getTracers({
      variables: {
        where: {
          flow: {
            process: {
              theme: prospectTheme,
            },
          },
        },
        take: FALLBACK_TRACERS_TAKE,
      },
      fetchPolicy: "network-only",
    });
    const tracersList = get(data?.tracers, "data", []);
    if (tracersList.length === 0) {
      failResolution();
      return;
    }

    const uniqueProcesses = uniqueB2cProcessesFromTracers(tracersList);
    if (uniqueProcesses.length === 0) {
      failResolution();
      return;
    }

    if (uniqueProcesses.length === 1) {
      const tracer = pickTracerForProcessId(tracersList, uniqueProcesses[0].id);
      if (tracer) {
        succeedResolution(tracer);
      } else {
        failResolution();
      }
      return;
    }

    const defaultTracers = tracersList.filter((tr) => tr.isDefault);
    const defaultProcessIds = [
      ...new Set(
        defaultTracers.map((tr) => get(tr, "flow.process.id")).filter(Boolean),
      ),
    ];
    if (defaultProcessIds.length === 1) {
      const tracer = pickTracerForProcessId(tracersList, defaultProcessIds[0]);
      if (tracer) {
        succeedResolution(tracer);
        return;
      }
    }

    openPicker(uniqueProcesses);
  }, [
    prospectTheme,
    getTracers,
    failResolution,
    succeedResolution,
    openPicker,
  ]);

  const resolveFromAssociatedProcess = useCallback(
    async (processRef) => {
      const tracer = await loadTracerForProcess(processRef);
      if (!tracer) {
        failResolution();
        return;
      }
      succeedResolution(tracer);
    },
    [loadTracerForProcess, failResolution, succeedResolution],
  );

  const startResolution = useCallback(async () => {
    let resolvedTracerId = tracerId;
    if (!resolvedTracerId && projectId) {
      const { data: projectData } = await getProjectBrokerTracerId({
        variables: { where: { id: projectId } },
        fetchPolicy: "network-only",
      });
      resolvedTracerId = get(projectData, "project.tracer.id");
    }

    if (resolvedTracerId) {
      const { data: tracerData } = await getTracer({
        variables: { where: { id: resolvedTracerId } },
        fetchPolicy: "network-only",
      });
      const contextTracer = tracerData?.tracer;
      const usesContextTracerDirectly =
        contextTracer?.type && contextTracer.type !== TRACER_TYPES.MANUAL;

      if (usesContextTracerDirectly) {
        succeedResolution(contextTracer);
        return;
      }

      const { data } = await getProcessConfigs({
        variables: {
          where: {
            type: PROCESS_CONFIG_TYPES.ASSOCIATED_PROCESSES,
            process: {
              flows: { tracers: { id: resolvedTracerId } },
            },
          },
        },
        fetchPolicy: "network-only",
      });
      const configs = get(data?.processConfigs, "data", []);
      const processes = get(configs, "[0].value.processes", []);

      if (processes.length === 0) {
        await resolveFromFallback();
      } else if (processes.length === 1) {
        await resolveFromAssociatedProcess(processes[0]);
      } else {
        openPicker(processes);
      }
    } else {
      await resolveFromFallback();
    }
  }, [
    tracerId,
    projectId,
    getTracer,
    getProcessConfigs,
    getProjectBrokerTracerId,
    resolveFromFallback,
    resolveFromAssociatedProcess,
    succeedResolution,
    openPicker,
  ]);

  const resolveProcessTracer = useCallback(() => {
    if (pendingResolveRef.current) {
      pendingResolveRef.current.reject(new Error(PROCESS_PICKER_CANCELLED));
      pendingResolveRef.current = null;
    }

    return new Promise((resolve, reject) => {
      pendingResolveRef.current = { resolve, reject };
      setStep(STEPS.LOADING);
      startResolution().catch((error) => {
        failResolution(error);
      });
    });
  }, [startResolution, failResolution]);

  const onSelectProcess = useCallback(
    (proc) => {
      setStep(STEPS.LOADING);
      void resolveFromAssociatedProcess(proc);
    },
    [resolveFromAssociatedProcess],
  );

  const onCancel = useCallback(() => {
    const pending = pendingResolveRef.current;
    pendingResolveRef.current = null;
    closeModal();
    if (pending) {
      pending.reject(new Error(PROCESS_PICKER_CANCELLED));
    }
  }, [closeModal]);

  const ProcessPickerModal = (
    <Modal
      title={pickProcessTitle}
      open={isModalOpen}
      onCancel={onCancel}
      footer={null}
      destroyOnClose
    >
      {step === STEPS.PICK_PROCESS ? (
        <ProcessPicker
          processes={associatedProcesses}
          onSelect={onSelectProcess}
        />
      ) : (
        <div className="d-flex justify--center p-24">
          <Spin />
        </div>
      )}
    </Modal>
  );

  return { resolveProcessTracer, ProcessPickerModal };
};

export const PROCESS_PICKER_CANCELLED_ERROR = PROCESS_PICKER_CANCELLED;

export default useQuoteProcessResolver;
